import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import _cloneDeep from 'lodash/cloneDeep';
import _trim from 'lodash/trim';
import getConfig from 'next/config';
import PropTypes from 'prop-types';

import AnonReviewFormPopup from 'components/anonReviewForm/anonReviewFormPopup';
import JobList from 'components/jobs/list';
import { dispatchSessionShowMoreSignupModal } from 'store/modals/actions';
import { getLocations, importStatic } from 'utils';
import { POPUP_SURVEY_SOURCE } from 'utils/constant';
import { CPP_SUBPAGES } from 'utils/constant';

import { SideCard } from '../../components/cards/cmpSideCard';
import ReviewBox from '../../components/companyCard/reviewBox';

import styles from './timeline.module.css';

const paths = [1, 2, 3, 4];
const {
  publicRuntimeConfig: { ZIPPIA_STATIC_SERVER },
} = getConfig();

const Item = ({ sentence, paragraph, containerClass, isFirst }) => {
  const [show, setShow] = useState(false);

  const onClick = () => {
    if (paragraph) {
      setShow((state) => !state);
    }
  };

  return (
    <div className={containerClass}>
      <div className={`d-block d-md-none ${isFirst ? 'icon-point-mobile' : 'icon-point-2'}`}>
        {paths.map((path) => (
          <span key={path} className={`path${path}`} />
        ))}
      </div>
      <div
        className={`d-none d-md-block no-before-icon ${
          isFirst ? 'icon-point-line' : 'icon-point-2'
        }`}
      >
        {paths.map((path) => (
          <span key={path} className={`path${path}`} />
        ))}
      </div>
      <div
        className={classnames(
          'ml-md-4 openSansBlack py-0 py-lg-3 w-100 d-flex align-items-center justify-content-between',
          styles.sentence,
          {
            [styles.extraSentence]: !isFirst,
          }
        )}
        onClick={onClick}
      >
        <p className={classnames('m-0', styles.text)}>{show ? paragraph : sentence}</p>
        {_trim(paragraph) !== _trim(sentence) && paragraph && (
          <div>
            <img
              src={importStatic('/ui-router/images/timeline-arrow-down.svg')}
              className={styles.arrow}
              style={{ transform: `rotateZ(${show ? 180 : 0}deg)` }}
              width="36"
              height="36"
            />
          </div>
        )}
      </div>
    </div>
  );
};

const CompanyTimeLineSection = (props) => {
  const {
    headerContent,
    companyLocation,
    companyLocationLink,
    companyFounders,
    companyTimeline,
    companyFoundedDate,
    dispatchSessionShowMoreSignupModal,
    user,
    jobListDismiss,
    criteria,
    companyId,
    companyName,
    companyLogo,
    displayFlagOfCompanyJobsPage,
    onOpenMainCppSurvey = () => {},
  } = props;
  const [sentenceList, setSentenceList] = useState(_cloneDeep(companyTimeline));
  const [listSize, setListSize] = useState(0);
  const [total, setTotal] = useState(0);
  const isUserLoggedIn = !!(user && user.isLoggedIn);
  const [showAnonReviewForm, setShowAnonReviewForm] = React.useState(false);

  const onClose = () => {
    if (!showAnonReviewForm) return;
    setShowAnonReviewForm(false);
    document.body.style.overflow = 'unset';
  };

  const openAnonReviewForm = () => {
    if (showAnonReviewForm) return;
    setShowAnonReviewForm(true);
    document.body.style.overflow = 'hidden';
  };

  useEffect(() => {
    setListSize(20);
    setTotal(companyTimeline.reduce((acc, { sentences }) => acc + sentences.length, 0));
  }, []);

  useEffect(() => {
    let j = 0;
    const list = [];
    for (let i = 0; i < companyTimeline.length; i++) {
      const { year, sentences } = companyTimeline[i];
      list.push({
        year,
        sentences: sentences.slice(0, listSize - j),
      });
      j += sentences.length;
      if (j >= listSize) {
        break;
      }
    }
    setSentenceList(list);
  }, [listSize]);

  function renderCompanyFounders() {
    return (
      <div className="d-flex flex-column">
        {companyFounders.map((person, index) =>
          index !== companyFounders.length - 1 ? (
            <span key={index}>{`${person.cleanPersonName},`}</span>
          ) : (
            <span key={index}>{person.cleanPersonName}</span>
          )
        )}
      </div>
    );
  }

  function setRightSide() {
    const RIGHT_SIDE = [];
    if (companyFoundedDate) {
      RIGHT_SIDE.push({
        id: 1,
        title: 'Founded',
        description: companyFoundedDate,
        icon: `${ZIPPIA_STATIC_SERVER}/images/cmp-history/founded.svg`,
        alt: 'Company Founded',
      });
    }
    if (companyLocation) {
      RIGHT_SIDE.push({
        id: 2,
        title: 'Headquarters',
        description: companyLocationLink ? (
          <a className="alink" href={companyLocationLink}>
            {companyLocation}
          </a>
        ) : (
          companyLocation
        ),
        icon: `${ZIPPIA_STATIC_SERVER}/images/cmp-history/headquarters.svg`,
        alt: 'Company Headquarter',
      });
    }
    if (companyFounders && companyFounders.length) {
      RIGHT_SIDE.push({
        id: 3,
        title: 'Founders',
        description: renderCompanyFounders(),
        icon: `${ZIPPIA_STATIC_SERVER}/images/career-demographics/average-employee-age.svg`,
        alt: 'Company Founders',
      });
    }
    if (!isUserLoggedIn) {
      RIGHT_SIDE.push({
        id: 4,
        isFullBodyLink: true,
        onClick: () => onOpenMainCppSurvey(POPUP_SURVEY_SOURCE.CPP_HISTORY_SUMMMARY_BOX),
        description: 'Get Updates for Jobs and News',
        icon: `/ui-router/images/company-history/get-updates.svg`,
      });
    }
    return RIGHT_SIDE;
  }
  const rightSide = setRightSide();

  const computeCssClass = (index1, index2) => {
    let className = 'd-flex company-timeline-sentence ';
    if (index2 > 0) {
      className += 'company-timeline-sentence-next ';
    }
    if (
      index1 === companyTimeline.length - 1 &&
      index2 === companyTimeline[companyTimeline.length - 1].sentences.length - 1
    ) {
      className += 'company-timeline-sentence-last';
    }
    return className;
  };

  const onShowMoreClick = () => {
    setListSize(listSize + 10);
    dispatchSessionShowMoreSignupModal();
  };

  return (
    <div className="company-timeline">
      <div className="company-timeline-left w-100">
        {headerContent}
        {sentenceList.map(({ year, sentences }, index1) => (
          <div key={index1} className="company-timeline-sentences-year">
            <div className="company-timeline-year">{year}</div>
            <div className="company-timeline-sentences w-100">
              {sentences.map(({ sentence, paragraph }, index2) => (
                <Item
                  key={index2}
                  sentence={sentence}
                  paragraph={paragraph}
                  containerClass={computeCssClass(index1, index2)}
                  isFirst={index2 === 0}
                />
              ))}
            </div>
          </div>
        ))}
        {total > 20 && listSize < total && (
          <a className="btn btn-show-more" onClick={onShowMoreClick}>
            Show More
          </a>
        )}
      </div>
      {rightSide.length && (
        <div className="company-timeline-right">
          <div className="z-mb-24">
            <ReviewBox
              newDesign
              companyID={companyId}
              companyName={companyName}
              companyLogo={companyLogo}
              customOnClick={openAnonReviewForm}
            />
          </div>
          {rightSide.map(
            ({ id, title, description, icon, alt, isFullBodyLink, onClick, ...rest }) => (
              <SideCard
                key={id}
                title={title}
                value={description}
                icon={icon}
                alt={alt}
                isFullBodyLink={isFullBodyLink}
                onClick={onClick}
                isTimeline={true}
                {...rest}
                noShadow
              />
            )
          )}
          {!jobListDismiss && (
            <div className="job-list-placeholder position-static z-mt-24">
              <JobList
                title={`${companyName} Jobs Nearby`}
                jobsPageIndexable={displayFlagOfCompanyJobsPage}
                origin={CPP_SUBPAGES.HISTORY}
                initialPayload={{
                  title: criteria.title,
                  companyID: [companyId],
                  locations: getLocations(user?.userLocation, criteria).map(({ city, state, stateAbbr }) => ({
                    city,
                    state: stateAbbr || state,
                  })),
                }}
                companyLink={criteria.companyUrl}
              />
            </div>
          )}
          <AnonReviewFormPopup
            companyName={companyName}
            companyID={companyId}
            show={showAnonReviewForm}
            onClose={onClose}
          />
        </div>
      )}
    </div>
  );
};

CompanyTimeLineSection.propTypes = {
  headerContent: PropTypes.element,
  companyTimeline: PropTypes.arrayOf(
    PropTypes.shape({
      year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      sentences: PropTypes.arrayOf(PropTypes.string),
    }).isRequired
  ).isRequired,
  companyLocation: PropTypes.string.isRequired,
  companyFounders: PropTypes.arrayOf(PropTypes.object),
  companyFoundedDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  companyId: PropTypes.number.isRequired,
  displayFlagOfCompanyJobsPage: PropTypes.bool.isRequired,
  companyName: PropTypes.string.isRequired,
  criteria: PropTypes.object.isRequired,
  onOpenMainCppSurvey: PropTypes.func,
};

const mapStateToProps = ({ user }) => {
  return {
    user,
  };
};

const mapDispatchToProps = {
  dispatchSessionShowMoreSignupModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(CompanyTimeLineSection);
